import * as Sentry from '@sentry/react-router';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';

import { type ReactNode } from 'react';
import type { Route } from './+types/root';
import './app.css';
import { GA_MEASUREMENT_ID, isAnalyticsEnabled } from './config/analytics';
import { ErrorPage } from './components/ErrorPage';
import { usePageTracking } from './hooks/usePageTracking';
import { Toaster } from './components/ui/sonner';
import FullScreenLoader from './pages/Loading/LoadingPage';

export const links: Route.LinksFunction = () => [
  { rel: 'icon', type: 'image/png', href: '/favicon.png' },
  { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@100..900&display=swap',
  },
];

export function Layout({ children }: { children: ReactNode }) {
  return (
    <html lang='ja'>
      <head>
        <meta charSet='utf-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <meta name='commit-sha' content={import.meta.env.VITE_GITHUB_SHA} />
        <Meta />
        <Links />
        {isAnalyticsEnabled && (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}', { send_page_view: false });
            `,
              }}
            />
          </>
        )}
      </head>
      <body>
        {children}
        <Toaster richColors />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  usePageTracking();
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return <ErrorPage title='404 - ページが見つかりません' description='' isNotFound />;
    }
    if (error.status === 401) {
      return (
        <ErrorPage
          title='401 - アクセス権限がありません'
          description='このページを閲覧する権限がありません。'
        />
      );
    }
    Sentry.captureException(error);
  } else {
    Sentry.captureException(error);
  }

  return (
    <ErrorPage title='予期せぬエラーが発生しました' description='システムエラーが発生しました。' />
  );
}

export function HydrateFallback() {
  return <FullScreenLoader />;
}
